import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Divider,
  message,
  Drawer
} from 'antd';
import {
  DollarOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/vi';
import { giftService, Gift, Event, GiftGiver } from '../../services/giftService';
import { DatePickerModal } from '../common/DatePickerModal';
import styles from '../common/DrawerModalMobile.module.css';

const { TextArea } = Input;
const { Option } = Select;

// Cấu hình locale tiếng Việt
dayjs.locale('vi');

interface AddGiftsModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingGift?: Gift | null;
  eventId?: string | null;
  giverId?: string | null;
}

export const AddGiftsModalMobile: React.FC<AddGiftsModalMobileProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingGift,
  eventId,
  giverId
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [localEvents, setLocalEvents] = useState<Event[]>([]);
  const [localGivers, setLocalGivers] = useState<GiftGiver[]>([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [currentDateField, setCurrentDateField] = useState<string>('');

  // Fetch events và givers khi modal mở
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [eventsResponse, giversResponse] = await Promise.all([
            giftService.getEvents(),
            giftService.getGiftGivers({ limit: 1000 }) // Lấy tất cả givers
          ]);
          setLocalEvents(eventsResponse.data);
          setLocalGivers(giversResponse.data);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };
      fetchData();
    }
  }, [isOpen]);

  // Reset form khi modal mở/đóng hoặc editingGift thay đổi
  useEffect(() => {
    if (isOpen) {
      if (editingGift) {
        // Edit mode
        form.setFieldsValue({
          event_id: editingGift.event_id,
          giver_id: editingGift.giver_id,
          amount: editingGift.amount,
          gift_type: editingGift.gift_type,
          gift_name: editingGift.gift_name,
          gift_value: editingGift.gift_value,
          notes: editingGift.notes,
          is_returned: editingGift.is_returned,
          return_date: editingGift.return_date ? dayjs(editingGift.return_date) : null,
          return_event_id: editingGift.return_event_id,
          return_amount: editingGift.return_amount,
          return_notes: editingGift.return_notes
        });
        if (editingGift.return_date) {
          setSelectedDate(dayjs(editingGift.return_date));
        }
      } else {
        // Create mode
        form.resetFields();
        form.setFieldsValue({
          gift_type: 'cash'
        });
        if (eventId) {
          form.setFieldsValue({ event_id: parseInt(eventId) });
        }
        if (giverId) {
          form.setFieldsValue({ giver_id: parseInt(giverId) });
        }
        setSelectedDate(dayjs());
      }
    }
  }, [isOpen, editingGift, form, eventId, giverId]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const data = {
        ...values,
        return_date: values.return_date ? values.return_date.format('YYYY-MM-DD') : null
      };

      if (editingGift) {
        await giftService.updateGift(editingGift.id, data);
        message.success('Cập nhật tiền mừng thành công');
      } else {
        await giftService.createGift(data);
        message.success('Tạo tiền mừng thành công');
      }

      onClose();
      onSuccess();
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      const errorMessage = error.response?.data?.error || error.message || 'Có lỗi xảy ra';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRelationshipLabel = (relationship: string) => {
    const labels: Record<string, string> = {
      family_internal: 'Gia đình nội',
      family_external: 'Gia đình ngoại',
      friend_bride: 'Bạn cô dâu',
      friend_groom: 'Bạn chú rể',
      colleague: 'Đồng nghiệp',
      business_partner: 'Đối tác kinh doanh',
      other: 'Khác'
    };
    return labels[relationship] || relationship;
  };



  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(date);
    form.setFieldsValue({ [currentDateField]: date });
    setShowDatePicker(false);
  };

  const handleDateClick = (fieldName: string) => {
    setCurrentDateField(fieldName);
    const currentValue = form.getFieldValue(fieldName);
    if (currentValue) {
      setSelectedDate(dayjs(currentValue));
    } else {
      setSelectedDate(dayjs());
    }
    setShowDatePicker(true);
  };

  return (
    <Drawer
      title={editingGift ? 'Chỉnh sửa tiền mừng' : 'Thêm tiền mừng mới'}
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      height="90%"
      className={styles.drawer}
      bodyStyle={{ padding: 0 }}
    >
      <div className={styles.drawerContent}>
        <div className={styles.drawerBody}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              gift_type: 'cash'
            }}
          >
            <div className={styles.formItem}>
              <Form.Item
                name="event_id"
                label="Sự kiện"
                rules={[{ required: true, message: 'Vui lòng chọn sự kiện' }]}
              >
                <Select 
                  placeholder="Chọn sự kiện" 
                  disabled={!!eventId}
                  className={styles.select}
                                 >
                   {localEvents.map(event => (
                     <Option key={event.id} value={event.id}>
                       {event.name} - {dayjs(event.event_date).format('DD/MM/YYYY')}
                     </Option>
                   ))}
                 </Select>
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="giver_id"
                label="Người mừng"
                rules={[{ required: true, message: 'Vui lòng chọn người mừng' }]}
              >
                <Select 
                  placeholder="Chọn người mừng" 
                  disabled={!!giverId}
                  showSearch
                  filterOption={(input, option) => {
                    const optionText = option?.children?.toString() || '';
                    return optionText.toLowerCase().includes(input.toLowerCase());
                  }}
                  optionFilterProp="children"
                  className={styles.select}
                                 >
                   {localGivers.map(giver => (
                     <Option key={giver.id} value={giver.id}>
                       {giver.full_name} - {getRelationshipLabel(giver.relationship)}
                     </Option>
                   ))}
                 </Select>
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="amount"
                label="Số tiền"
                rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
              >
                <InputNumber
                  className={styles.inputNumber}
                  placeholder="Nhập số tiền"
                  formatter={(value: number | string | undefined) => {
                    if (value === undefined || value === null || value === '') return '';
                    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                  }}
                  parser={(value: string | undefined) => {
                    if (!value) return '';
                    return value.replace(/\$\s?|(,*)/g, '');
                  }}
                  min={0}
                  prefix={<DollarOutlined />}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="gift_type"
                label="Loại"
                rules={[{ required: true, message: 'Vui lòng chọn loại' }]}
              >
                <Select placeholder="Chọn loại" className={styles.select}>
                  <Option value="cash">Tiền mặt</Option>
                  <Option value="gift">Quà tặng</Option>
                  <Option value="other">Khác</Option>
                </Select>
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="gift_name"
                label="Tên quà tặng"
              >
                <Input placeholder="Tên quà tặng (nếu có)" />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="gift_value"
                label="Giá trị quà"
              >
                <InputNumber
                  className={styles.inputNumber}
                  placeholder="Giá trị quà"
                  formatter={(value: number | string | undefined) => {
                    if (value === undefined || value === null || value === '') return '';
                    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                  }}
                  parser={(value: string | undefined) => {
                    if (!value) return '';
                    return value.replace(/\$\s?|(,*)/g, '');
                  }}
                  min={0}
                  prefix={<DollarOutlined />}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="notes"
                label="Ghi chú"
              >
                <TextArea 
                  rows={3} 
                  placeholder="Nhập ghi chú hoặc click vào các nút bên dưới"
                  className={styles.textArea}
                />
              </Form.Item>
            </div>
            
            <div className={styles.formItem}>
              <Form.Item label="Ghi chú mẫu">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    'Không đi'
                  ].map((note, index) => (
                    <Button
                      key={index}
                      size="small"
                      type="dashed"
                      onClick={() => {
                        const currentNotes = form.getFieldValue('notes') || '';
                        const newNotes = currentNotes ? `${currentNotes}, ${note}` : note;
                        form.setFieldsValue({ notes: newNotes });
                      }}
                      style={{ marginBottom: '4px' }}
                    >
                      {note}
                    </Button>
                  ))}
                </div>
              </Form.Item>
            </div>

            {editingGift && (
              <>
                <Divider>Thông tin đáp lễ</Divider>
                
                <div className={styles.formItem}>
                  <Form.Item
                    name="is_returned"
                    label="Đã đáp lễ"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </div>

                <div className={styles.formItem}>
                  <Form.Item
                    name="return_date"
                    label="Ngày đáp lễ"
                  >
                    <div className={styles.dateInputWrapper}>
                      <Input
                        className={styles.dateInput}
                        placeholder="Chọn ngày đáp lễ"
                        value={form.getFieldValue('return_date') ? dayjs(form.getFieldValue('return_date')).format('DD/MM/YYYY') : ''}
                        readOnly
                        onClick={() => handleDateClick('return_date')}
                        suffix={<CalendarOutlined style={{ color: '#666' }} />}
                      />
                    </div>
                  </Form.Item>
                </div>

                <div className={styles.formItem}>
                  <Form.Item
                    name="return_event_id"
                    label="Sự kiện đáp lễ"
                  >
                                         <Select placeholder="Chọn sự kiện đáp lễ" allowClear className={styles.select}>
                       {localEvents.map(event => (
                         <Option key={event.id} value={event.id}>
                           {event.name}
                         </Option>
                       ))}
                     </Select>
                  </Form.Item>
                </div>
                <div className={styles.formItem}>
                  <Form.Item
                    name="return_amount"
                    label="Số tiền đáp lễ"
                  >
                    <InputNumber
                      className={styles.inputNumber}
                      placeholder="Số tiền đáp lễ"
                      formatter={(value: number | string | undefined) => {
                        if (value === undefined || value === null || value === '') return '';
                        return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                      }}
                      parser={(value: string | undefined) => {
                        if (!value) return '';
                        return value.replace(/\$\s?|(,*)/g, '');
                      }}
                      min={0}
                      prefix={<DollarOutlined />}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </div>

                <div className={styles.formItem}>
                  <Form.Item
                    name="return_notes"
                    label="Ghi chú đáp lễ"
                  >
                    <TextArea 
                      rows={2} 
                      placeholder="Ghi chú về việc đáp lễ"
                      className={styles.textArea}
                    />
                  </Form.Item>
                </div>
              </>
            )}
          </Form>
        </div>

        <div className={styles.drawerFooter}>
          <div className={styles.drawerFooterRow}>
          <Button 
              onClick={onClose}
              className={`${styles.drawerFooterButton} ${styles.default}`}
            >
              Hủy
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={loading}
              onClick={() => form.submit()}
              className={`${styles.drawerFooterButton} ${styles.primary}`}
            >
              {editingGift ? 'Cập nhật' : 'Thêm tiền mừng'}
            </Button>

          </div>
        </div>
      </div>

      {/* Custom Date Picker Modal */}
      <DatePickerModal
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelect={handleDateSelect}
        selectedDate={selectedDate}
        title={currentDateField === 'return_date' ? "Chọn ngày đáp lễ" : "Chọn ngày"}
      />
    </Drawer>
  );
};
